import { useState, useEffect, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Typography,
  Box,
  Paper,
  Button,
  Stack,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { PerformanceComparisonTableProps } from "../../../helpers/types";
import { useColumnPeriods } from "../../../hooks/useColumnPeriods";
import PerformancePeriodsSelectorModal from "./PerformancePeriodsSelectorModal";
import { useStrategyQuery } from "hooks/useStrategyQuery";
import { useAccountQuery } from "hooks/useAccountQuery";
import { useGetReportQuery } from "../../../features/trading/accounts";
import { useReportTrigger } from "../../../containers/ReportPageDeprecated";
import { useStatisticGrid } from "../../../hooks/useStatisticGrid";
import { asPercentage } from "../../../components/ui/formatters";

const PerformanceComparisonTable = ({
  dateRange,
  onColumnPeriodsChange,
}: PerformanceComparisonTableProps & {
  onColumnPeriodsChange: (periods: any[]) => void;
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { columnPeriods, setColumnPeriods } = useColumnPeriods({ dateRange });
  const {
    data: stockData,
    isLoading: isStockLoading,
    isFetching: isStockFetching,
  } = useStrategyQuery();
  const { data: accountData } = useAccountQuery();
  const { shouldFetch } = useReportTrigger();
  const { gridColumns, buildRow, gridStyles } = useStatisticGrid({
    columnPeriods,
  });

  // Create query parameters
  const queryParams = useMemo(() => {
    if (
      stockData?.id &&
      accountData?.id &&
      shouldFetch &&
      columnPeriods.length > 0
    ) {
      return {
        account_ids: [accountData.id],
        strategy_ids: [stockData.id],
        periods: columnPeriods.map((col) => ({
          name: col.name,
          period: col.period,
        })),
      };
    }
    return null;
  }, [stockData?.id, accountData?.id, columnPeriods, shouldFetch]);

  // Use the query hook
  const {
    data: reportData,
    isLoading: isReportLoading,
    error: reportError,
  } = useGetReportQuery(queryParams!, {
    skip: !queryParams,
  });

  useEffect(() => {
    onColumnPeriodsChange(columnPeriods);
  }, [columnPeriods, onColumnPeriodsChange]);

  // Memoize the rows calculation
  const rows = useMemo(() => {
    if (reportData?.data?.income_summary) {
      const incomeSummary = reportData.data.income_summary;

      return [
        buildRow(
          `${stockData?.name} Optic *`,
          columnPeriods.map((period) => {
            const periodData = incomeSummary.find(
              (item: any) => item.name === period.name,
            );
            return periodData
              ? asPercentage(periodData.value.time_weighted_return)
              : "0.00%";
          }),
          1,
        ),
        buildRow(
          `${stockData?.name} Stock`,
          columnPeriods.map((period) => {
            const periodData = incomeSummary.find(
              (item: any) => item.name === period.name,
            );
            return periodData
              ? asPercentage(periodData.value.time_weighted_return_stock)
              : "0.00%";
          }),
          2,
        ),
      ];
    }
    return [
      buildRow(
        `${stockData?.name} Optic *`,
        columnPeriods.map(() => "0.00%"),
        1,
      ),
      buildRow(
        `${stockData?.name} Stock`,
        columnPeriods.map(() => "0.00%"),
        2,
      ),
    ];
  }, [
    reportData?.data?.income_summary,
    columnPeriods,
    buildRow,
    stockData?.name,
  ]);

  const isLoading = isStockLoading || isStockFetching || isReportLoading;

  if (isLoading) {
    return (
      <Box
        sx={{
          mt: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (reportError) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Performance Comparison
        </Typography>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography color="error">
            Error loading performance data. Please try again.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<EditIcon />}
          onClick={() => setModalOpen(true)}
        >
          Edit Periods
        </Button>
      </Stack>
      <Paper elevation={1}>
        <Typography variant="h6" sx={{ my: 2 }}>
          Performance of {stockData?.name} Optic vs {stockData?.name} Stock
        </Typography>
        <DataGrid
          autoHeight
          rows={rows}
          columns={gridColumns}
          hideFooter
          disableColumnMenu
          disableRowSelectionOnClick
          disableColumnFilter
          sx={gridStyles}
        />
      </Paper>
      <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
        * Includes the performance of the underlying stock in addition to the
        Optic option overlay strategy.
      </Typography>
      <PerformancePeriodsSelectorModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        columnPeriods={columnPeriods}
        onChange={setColumnPeriods}
        dateRange={dateRange}
      />
    </Box>
  );
};

export default PerformanceComparisonTable;
